import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getHRSettingsMasterData } from "../../services/employeeService";
import {
  applyLeaveRequest,
  ensureLeaveBalances,
  getLeaveManagementData,
  updateLeaveRequestStatus,
} from "../../services/leaveManagementService";
import {
  buildLeaveApplyNotificationPayload,
  buildLeaveStatusNotificationPayload,
  createNotification,
} from "../../services/notificationService";
import { getCurrentUser } from "../../utils/userContext";
import useRole from "../../hooks/useRole";

import "../../styles/hrPageShell.css";
const API_DEFAULT_EMPLOYEE = "EMP001";
const DEFAULT_ACTION_BY = "HR Admin";
const STATUS_OPTIONS = [
  "All",
  "Pending",
  "Approved",
  "Rejected",
  "Cancelled",
  "Cancellation Requested",
  "Cancelled After Approval",
];
const STATUS_LABELS = {
  pending: "Pending",
  approved: "Approved",
  rejected: "Rejected",
  cancelled: "Cancelled",
  "cancellation requested": "Cancellation Requested",
  "cancelled after approval": "Cancelled After Approval",
};
const APPROVER_ROLES = ["Manager", "HR", "HRAdmin", "Admin"];

function extractValue(value) {
  if (value === undefined || value === null) return "";
  if (["string", "number", "boolean"].includes(typeof value)) return String(value);
  if (Array.isArray(value)) return value.map(extractValue).filter(Boolean).join("; ");
  if (typeof value === "object") return extractValue(value.Value || value.value || value.Title || value.Email || value.DisplayName || "");
  return String(value);
}
function getField(record, ...names) {
  if (!record || typeof record !== "object") return "";
  for (const name of names) {
    if (record[name] !== undefined && record[name] !== null) return record[name];
  }
  const normalized = Object.keys(record).reduce((acc, key) => {
    acc[String(key).toLowerCase().replace(/[^a-z0-9]/g, "")] = record[key];
    return acc;
  }, {});
  for (const name of names) {
    const key = String(name).toLowerCase().replace(/[^a-z0-9]/g, "");
    if (normalized[key] !== undefined && normalized[key] !== null) return normalized[key];
  }
  return "";
}
function isTruthy(value) {
  return value === true || value === "true" || value === "True" || value === "Yes" || value === "yes" || value === 1 || value === "1";
}
function toNumber(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}
function normalizeStatus(status) {
  return extractValue(status).trim().toLowerCase().replace(/\s+/g, " ");
}
function getCanonicalStatus(status, fallback = "Pending") {
  const key = normalizeStatus(status);
  return STATUS_LABELS[key] || extractValue(status).trim() || fallback;
}
function normalizeTextField(value, fallback = "") {
  const text = extractValue(value).trim();
  return text || fallback;
}
function normalizeKey(value) {
  return extractValue(value).toUpperCase().replace(/[^A-Z0-9]/g, "");
}
function normalizePolicyIdForCompare(value) {
  let text = extractValue(value).toUpperCase().trim();
  if (!text) return "";
  text = text.replace(/^LVPOL-/, "LPOL-");
  text = text.replace(/-IND-/g, "-IN-");
  text = text.replace(/-FULLTIME-/g, "-FT-");
  text = text.replace(/-FULL-TIME-/g, "-FT-");
  text = text.replace(/-PARTTIME-/g, "-PT-");
  text = text.replace(/-PART-TIME-/g, "-PT-");
  return normalizeKey(text);
}
function normalizeLeaveTypeForCompare(value) {
  return extractValue(value).toUpperCase().replace(/[^A-Z0-9]/g, "");
}
function formatDate(value) {
  const text = extractValue(value);
  if (!text) return "";
  const date = new Date(text);
  if (Number.isNaN(date.getTime())) return text;
  return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}
function getDateInputValue(value) {
  const text = extractValue(value);
  if (!text) return "";
  const date = new Date(text);
  if (Number.isNaN(date.getTime())) return text;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}
function dateOnlyTime(value) {
  const text = extractValue(value);
  if (!text) return Number.NaN;
  const date = new Date(text);
  if (Number.isNaN(date.getTime())) return Number.NaN;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}
function calculateLeaveDays(fromDate, toDate) {
  if (!fromDate || !toDate) return 0;
  const start = new Date(fromDate);
  const end = new Date(toDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return 0;
  return Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
}
function belongsToEmployee(record = {}, employeeCode = "") {
  const code = extractValue(employeeCode).trim();
  if (!code) return true;
  return extractValue(record.employeeCode || record.EmployeeCode).trim() === code;
}
function getBalanceAvailable(balance) {
  return toNumber(balance.availableDays ?? balance.available, 0);
}
function splitBalanceTitle(title = "") {
  const text = extractValue(title);
  const match = text.match(/^(.+?)-(LPOL-[A-Z]+-[A-Z]+-\d+)-(.+)-(\d{4})$/i);
  if (!match) return {};
  return {
    employeeCode: match[1],
    leavePolicyId: match[2],
    leaveType: match[3],
    leaveYear: toNumber(match[4], new Date().getFullYear()),
  };
}
function getArray(masterData, ...names) {
  for (const name of names) {
    const direct = getField(masterData, name);
    if (Array.isArray(direct)) return direct;
    if (typeof direct === "string") {
      try {
        const parsed = JSON.parse(direct);
        if (Array.isArray(parsed)) return parsed;
      } catch {
        // ignore
      }
    }
  }
  return [];
}
function getLeavePolicyRows(masterData = {}) {
  return getArray(masterData, "leavePolicies", "LeavePolicies", "leave policies", "HR_LeavePolicies");
}
function getPolicyNameForId(masterData = {}, leavePolicyId = "") {
  const target = normalizePolicyIdForCompare(leavePolicyId);
  const policy = getLeavePolicyRows(masterData).find((row) => normalizePolicyIdForCompare(getField(row, "LeavePolicyId", "LeavePolicyID", "Leave Policy ID", "Title")) === target);
  return extractValue(getField(policy, "PolicyName", "Policy Name", "Title"));
}
function repairLeaveRequests(rows = []) {
  return (rows || []).map((request) => {
    const canonicalStatus = getCanonicalStatus(request.status || request.Status, "Pending");
    const requestedOn = extractValue(request.cancellationRequestedOn || request.CancellationRequestedOn).trim();
    const rejectedOn = extractValue(request.cancellationRejectedOn || request.CancellationRejectedOn).trim();
    const flag = getField(request, "isCancellationRequested", "IsCancellationRequested", "isCancelationRequested", "IsCancelationRequested", "cancellationRequested", "CancellationRequested");
    const cancellationRequested = isTruthy(flag) || Boolean(requestedOn) || Boolean(rejectedOn) || ["cancellation requested", "cancelled after approval"].includes(normalizeStatus(canonicalStatus));
    return {
      ...request,
      employeeCode: extractValue(request.employeeCode || request.EmployeeCode),
      employeeName: extractValue(request.employeeName || request.EmployeeName),
      leavePolicyId: extractValue(request.leavePolicyId || request.LeavePolicyId),
      leaveType: extractValue(request.leaveType || request.LeaveType),
      leaveCategory: normalizeTextField(request.leaveCategory || request.LeaveCategory),
      fromDate: request.fromDate || request.FromDate || "",
      toDate: request.toDate || request.ToDate || "",
      reason: extractValue(request.reason || request.Reason),
      status: canonicalStatus,
      Status: canonicalStatus,
      chargeableDays: toNumber(request.chargeableDays ?? request.ChargeableDays ?? request.days ?? request.Days ?? request.requestedDays ?? request.RequestedDays, 0),
      requestedDays: toNumber(request.requestedDays ?? request.RequestedDays ?? request.days ?? request.Days, 0),
      days: toNumber(request.chargeableDays ?? request.ChargeableDays ?? request.days ?? request.Days ?? request.requestedDays ?? request.RequestedDays, 0),
      appliedOn: request.appliedOn || request.AppliedOn || "",
      isCancellationRequested: cancellationRequested,
      IsCancellationRequested: cancellationRequested,
    };
  });
}
function repairLeaveBalanceRows(balances = [], rules = [], assignment = {}, employeeCode = "") {
  const assignmentPolicyId = assignment?.leavePolicyId || assignment?.LeavePolicyId || "";
  const ruleByType = new Map((rules || []).map((rule) => [normalizeLeaveTypeForCompare(rule.leaveType), rule]));
  const repaired = (balances || []).map((balance) => {
    const fromTitle = splitBalanceTitle(balance.title || balance.Title || "");
    let leavePolicyId = balance.leavePolicyId || balance.LeavePolicyId || "";
    let leaveType = balance.leaveType || balance.LeaveType || "";
    if (!leavePolicyId && normalizePolicyIdForCompare(leaveType)) {
      leavePolicyId = leaveType;
      leaveType = "";
    }
    if (!leavePolicyId) leavePolicyId = fromTitle.leavePolicyId || assignmentPolicyId;
    if (!leaveType) leaveType = fromTitle.leaveType || "";
    const rule = ruleByType.get(normalizeLeaveTypeForCompare(leaveType));
    return {
      ...balance,
      employeeCode: balance.employeeCode || balance.EmployeeCode || fromTitle.employeeCode || employeeCode,
      employeeName: balance.employeeName || balance.EmployeeName || assignment?.employeeName || assignment?.EmployeeName || "",
      leavePolicyId,
      LeavePolicyId: leavePolicyId,
      leaveType,
      LeaveType: leaveType,
      leaveCategory: balance.leaveCategory || balance.LeaveCategory || rule?.leaveCategory || "",
      LeaveCategory: balance.leaveCategory || balance.LeaveCategory || rule?.leaveCategory || "",
      leaveYear: balance.leaveYear || balance.LeaveYear || fromTitle.leaveYear || new Date().getFullYear(),
      entitledDays: toNumber(balance.entitledDays ?? balance.EntitledDays ?? rule?.entitledDays, 0),
      availableDays: toNumber(balance.availableDays ?? balance.AvailableDays ?? balance.available, 0),
      usedDays: toNumber(balance.usedDays ?? balance.UsedDays ?? balance.used, 0),
      pendingDays: toNumber(balance.pendingDays ?? balance.PendingDays ?? balance.pending, 0),
    };
  });
  const existingKeys = new Set(repaired.map((row) => normalizeLeaveTypeForCompare(row.leaveType)));
  for (const rule of rules || []) {
    const key = normalizeLeaveTypeForCompare(rule.leaveType);
    if (!key || existingKeys.has(key)) continue;
    repaired.push({
      id: `preview-${employeeCode}-${assignmentPolicyId}-${rule.leaveType}`,
      employeeCode,
      leavePolicyId: assignmentPolicyId,
      LeavePolicyId: assignmentPolicyId,
      leaveType: rule.leaveType,
      LeaveType: rule.leaveType,
      leaveCategory: rule.leaveCategory,
      LeaveCategory: rule.leaveCategory,
      leaveYear: new Date().getFullYear(),
      entitledDays: toNumber(rule.entitledDays, 0),
      availableDays: toNumber(rule.entitledDays, 0),
      usedDays: 0,
      pendingDays: 0,
      isPreview: true,
    });
  }
  return repaired;
}
function repairActivityLogs(rows = []) {
  return (rows || []).map((log) => ({
    ...log,
    employeeCode: extractValue(log.employeeCode || log.EmployeeCode),
    leaveType: extractValue(log.leaveType || log.LeaveType),
    actionType: extractValue(log.actionType || log.ActionType),
    previousStatus: extractValue(log.previousStatus || log.PreviousStatus),
    newStatus: extractValue(log.newStatus || log.NewStatus),
    days: toNumber(log.days ?? log.Days, 0),
    actionBy: extractValue(log.actionBy || log.ActionBy),
    actionByRole: extractValue(log.actionByRole || log.ActionByRole),
    actionOn: log.actionOn || log.ActionOn || "",
    remarks: extractValue(log.remarks || log.Remarks),
  }));
}
function buildFallbackAssignmentFromBalances(balances = [], employeeCode = "") {
  const first = (balances || []).find((balance) => belongsToEmployee(balance, employeeCode)) || balances?.[0] || null;
  if (!first) return null;
  return {
    employeeCode,
    leavePolicyId: first.leavePolicyId || first.LeavePolicyId || "",
    LeavePolicyId: first.leavePolicyId || first.LeavePolicyId || "",
    attendancePolicyId: "",
    AttendancePolicyId: "",
    holidayCalendarId: first.holidayCalendarId || first.HolidayCalendarId || "",
    weeklyOffPatternId: first.weeklyOffPatternId || first.WeeklyOffPatternId || "",
    legalEntityCode: first.legalEntityCode || first.LegalEntityCode || "",
    locationCode: first.locationCode || first.LocationCode || "",
    departmentCode: first.departmentCode || first.DepartmentCode || "",
    workUnitCode: first.workUnitCode || first.WorkUnitCode || "",
    source: "GetData balances fallback",
  };
}
function buildFallbackRulesFromBalances(balances = []) {
  const seen = new Set();
  return (balances || []).filter((balance) => {
    const key = `${normalizePolicyIdForCompare(balance.leavePolicyId || balance.LeavePolicyId)}-${normalizeLeaveTypeForCompare(balance.leaveType || balance.LeaveType)}`;
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  }).map((balance) => ({
    leavePolicyId: balance.leavePolicyId || balance.LeavePolicyId || "",
    leaveType: balance.leaveType || balance.LeaveType || "",
    leaveCategory: normalizeTextField(balance.leaveCategory || balance.LeaveCategory),
    entitledDays: toNumber(balance.entitledDays ?? balance.EntitledDays, 0),
    isActive: true,
  }));
}
function looksLikeRoleLabel(value = "", employeeCode = "") {
  const text = extractValue(value).trim();
  const code = extractValue(employeeCode).trim();
  if (!text) return false;
  if (code && text.toUpperCase() === code.toUpperCase()) return true;
  return /^(Admin|Employee|Manager|HR|HRAdmin)\s*-\s*EMP/i.test(text);
}
function cleanEmployeeName(value = "", employeeCode = "") {
  const text = extractValue(value).trim();
  if (!text || looksLikeRoleLabel(text, employeeCode)) return "";
  return text;
}
function getBestEmployeeDisplayName({ employeeCode = "", selfData = {}, assignment = {}, balances = [], requests = [] } = {}) {
  const candidates = [
    selfData?.employeeName,
    selfData?.EmployeeName,
    selfData?.employee?.employeeName,
    selfData?.employee?.EmployeeName,
    selfData?.employee?.fullName,
    selfData?.employee?.FullName,
    selfData?.employee?.firstName,
    selfData?.employee?.FirstName,
    assignment?.employeeName,
    assignment?.EmployeeName,
    ...(balances || []).map((balance) => balance.employeeName || balance.EmployeeName),
    ...(requests || []).map((request) => request.employeeName || request.EmployeeName),
  ];
  for (const candidate of candidates) {
    const cleaned = cleanEmployeeName(candidate, employeeCode);
    if (cleaned) return cleaned;
  }
  return "";
}
function isBlockingLeaveStatus(status) {
  const text = normalizeStatus(status);
  return text === "pending" || text === "approved" || text === "cancellation requested";
}
function findOverlappingLeaveRequest(leaveRequests = [], employeeCode = "", fromDate = "", toDate = "") {
  const code = extractValue(employeeCode).trim();
  const requestedStart = dateOnlyTime(fromDate);
  const requestedEnd = dateOnlyTime(toDate);
  if (!code || Number.isNaN(requestedStart) || Number.isNaN(requestedEnd)) return null;
  return leaveRequests.find((request) => {
    if (extractValue(request.employeeCode).trim() !== code) return false;
    if (!isBlockingLeaveStatus(request.status)) return false;
    const existingStart = dateOnlyTime(request.fromDate);
    const existingEnd = dateOnlyTime(request.toDate);
    if (Number.isNaN(existingStart) || Number.isNaN(existingEnd)) return false;
    return existingStart <= requestedEnd && requestedStart <= existingEnd;
  }) || null;
}
function hasCancellationRequested(request) {
  return isTruthy(getField(request, "isCancellationRequested", "IsCancellationRequested")) || ["cancellation requested", "cancelled after approval"].includes(normalizeStatus(request?.status));
}
function getRequestId(request) {
  return String(request?.id || request?.ID || "");
}
function canApproveOrReject(request, role) {
  return APPROVER_ROLES.includes(role) && normalizeStatus(request.status) === "pending";
}
function canCancelPending(request, role, currentEmployeeCode) {
  if (normalizeStatus(request.status) !== "pending") return false;
  if (APPROVER_ROLES.includes(role)) return true;
  return extractValue(request.employeeCode).trim() === extractValue(currentEmployeeCode).trim();
}
function canRequestCancellation(request, role, currentEmployeeCode) {
  return role === "Employee" && normalizeStatus(request.status) === "approved" && !hasCancellationRequested(request) && extractValue(request.employeeCode).trim() === extractValue(currentEmployeeCode).trim();
}
function canActOnCancellation(request, role) {
  return APPROVER_ROLES.includes(role) && normalizeStatus(request.status) === "cancellation requested";
}
function getActionBy(role, currentEmployeeCode) {
  return role === "Employee" ? currentEmployeeCode : DEFAULT_ACTION_BY;
}
function getActionLabel(action) {
  return {
    approve: "Approve",
    reject: "Reject",
    cancelPending: "Cancel Pending",
    requestCancellation: "Request Cancellation",
    approveCancellation: "Approve Cancellation",
    rejectCancellation: "Reject Cancellation",
  }[action] || action;
}
function getStatusStyle(status) {
  switch (status) {
    case "Approved": return { background: "#dcfce7", color: "#166534", border: "1px solid #86efac" };
    case "Rejected": return { background: "#fee2e2", color: "#991b1b", border: "1px solid #fca5a5" };
    case "Cancellation Requested": return { background: "#e0f2fe", color: "var(--fm-page-accent)", border: "1px solid #7dd3fc" };
    case "Cancelled After Approval": return { background: "#f3e8ff", color: "#6b21a8", border: "1px solid #c084fc" };
    case "Cancelled": return { background: "#f3f4f6", color: "#374151", border: "1px solid #d1d5db" };
    default: return { background: "#fef3c7", color: "#92400e", border: "1px solid #fcd34d" };
  }
}

function LeaveManagement() {
  const roleInfo = useRole();
  const currentUserObj = getCurrentUser();
  const currentRole = currentUserObj?.role || roleInfo?.role || "Employee";
  const currentEmployeeCode = currentUserObj?.employeeCode || API_DEFAULT_EMPLOYEE;
  const currentEmployeeName = cleanEmployeeName(
    currentUserObj?.employeeName ||
    currentUserObj?.fullName ||
    currentUserObj?.displayName ||
    currentUserObj?.name ||
    "",
    currentEmployeeCode
  );
  const currentLegalEntityCode = currentUserObj?.legalEntityCode || currentUserObj?.LegalEntityCode || "";
  const currentDepartmentCode = currentUserObj?.departmentCode || currentUserObj?.DepartmentCode || "";
  const currentWorkUnitCode = currentUserObj?.workUnitCode || currentUserObj?.WorkUnitCode || "";
  const isApprover = APPROVER_ROLES.includes(currentRole);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [actionSavingId, setActionSavingId] = useState("");
  const [message, setMessage] = useState("");
  const [filterStatus, setFilterStatus] = useState("All");
  const [masterData, setMasterData] = useState({});
  const [currentAssignment, setCurrentAssignment] = useState(null);
  const [assignmentLeaveRules, setAssignmentLeaveRules] = useState([]);
  const [myLeaveRequests, setMyLeaveRequests] = useState([]);
  const [myLeaveBalances, setMyLeaveBalances] = useState([]);
  const [myActivityLogs, setMyActivityLogs] = useState([]);
  const [approvalRequests, setApprovalRequests] = useState([]);
  const [approvalActivityLogs, setApprovalActivityLogs] = useState([]);
  const [formData, setFormData] = useState({ employeeName: "", leaveType: "", fromDate: "", toDate: "", reason: "" });
  const [resolvedEmployeeName, setResolvedEmployeeName] = useState("");
  const lastAutoLoadKeyRef = useRef("");
  const loadRunSeqRef = useRef(0);

  const leaveDays = useMemo(() => calculateLeaveDays(formData.fromDate, formData.toDate), [formData.fromDate, formData.toDate]);
  const assignmentLeavePolicyId = currentAssignment?.leavePolicyId || currentAssignment?.LeavePolicyId || "";
  const assignmentAttendancePolicyId = currentAssignment?.attendancePolicyId || currentAssignment?.AttendancePolicyId || "";
  const assignedLeavePolicyName = useMemo(() => getPolicyNameForId(masterData, assignmentLeavePolicyId), [masterData, assignmentLeavePolicyId]);
  const loadedLeavePolicyCount = useMemo(() => getLeavePolicyRows(masterData).length, [masterData]);
  const selectedBalance = useMemo(() => {
    return getBalanceForLeaveType(myLeaveBalances, currentEmployeeCode, assignmentLeavePolicyId, formData.leaveType);
  }, [myLeaveBalances, currentEmployeeCode, assignmentLeavePolicyId, formData.leaveType]);
  const overlapWarning = useMemo(() => findOverlappingLeaveRequest(myLeaveRequests, currentEmployeeCode, formData.fromDate, formData.toDate), [myLeaveRequests, currentEmployeeCode, formData.fromDate, formData.toDate]);
  const insufficientBalanceWarning = useMemo(() => selectedBalance && leaveDays ? leaveDays > getBalanceAvailable(selectedBalance) : false, [selectedBalance, leaveDays]);
  const approvalFilteredRequests = useMemo(() => {
    if (filterStatus === "All") return approvalRequests;
    return approvalRequests.filter((item) => item.status === filterStatus);
  }, [approvalRequests, filterStatus]);
  const summary = useMemo(() => ({
    myAvailable: myLeaveBalances.reduce((sum, item) => sum + getBalanceAvailable(item), 0),
    myPending: myLeaveRequests.filter((item) => item.status === "Pending").length,
    approvalPending: approvalRequests.filter((item) => item.status === "Pending").length,
    approvalCancellation: approvalRequests.filter((item) => item.status === "Cancellation Requested").length,
    approvalsTotal: approvalRequests.length,
  }), [myLeaveBalances, myLeaveRequests, approvalRequests]);

  function getBalanceForLeaveType(leaveBalances = [], employeeCode = "", leavePolicyId = "", leaveType = "") {
    const code = extractValue(employeeCode).trim();
    const policyKey = normalizePolicyIdForCompare(leavePolicyId);
    const typeKey = normalizeLeaveTypeForCompare(leaveType);
    return leaveBalances.find((balance) => extractValue(balance.employeeCode).trim() === code && normalizePolicyIdForCompare(balance.leavePolicyId) === policyKey && normalizeLeaveTypeForCompare(balance.leaveType) === typeKey);
  }

  const loadLeavePage = useCallback(async ({ silent = false } = {}) => {
    if (!currentEmployeeCode) return;

    const runId = loadRunSeqRef.current + 1;
    loadRunSeqRef.current = runId;

    try {
      setLoading(true);
      if (!silent) setMessage("");

      const warnings = [];

      // 1) Load SELF data first. This is the only mandatory call for every user.
      // Do not run self and approval GetData in parallel because both hit the same flow/list set.
      let selfData = { leaveRequests: [], leaveBalances: [], leaveActivityLog: [] };
      try {
        selfData = await getLeaveManagementData({
          employeeCode: currentEmployeeCode,
          role: "Employee",
          viewMode: "self",
          includeInactive: false,
        });
      } catch (error) {
        warnings.push(error.message || "Self leave data failed.");
      }
      if (runId !== loadRunSeqRef.current) return;

      const selfRequests = repairLeaveRequests(selfData.leaveRequests || []).filter((request) => belongsToEmployee(request, currentEmployeeCode));
      const selfBalancesFromGetData = selfData.leaveBalances || [];
      const selfLogs = repairActivityLogs(selfData.leaveActivityLog || []).filter((log) => belongsToEmployee(log, currentEmployeeCode));

      // 2) Ensure balances only if GetData has no balances. This avoids a second heavy flow call on every page open.
      let ensureData = null;
      if (selfBalancesFromGetData.length === 0) {
        try {
          ensureData = await ensureLeaveBalances({
            employeeCode: currentEmployeeCode,
            leaveYear: new Date().getFullYear(),
            requestedBy: getActionBy("Employee", currentEmployeeCode),
            requestedByRole: "Employee",
          });
        } catch (error) {
          warnings.push(`Failed to initialize leave balances: ${error.message || "An unexpected error occurred."}`);
        }
      }
      if (runId !== loadRunSeqRef.current) return;

      const selfBalancesRaw = selfBalancesFromGetData.length ? selfBalancesFromGetData : (ensureData?.leaveBalances || []);
      const fallbackAssignment = buildFallbackAssignmentFromBalances(selfBalancesRaw, currentEmployeeCode);
      const assignment = ensureData?.assignment || fallbackAssignment || null;
      const fallbackRules = buildFallbackRulesFromBalances(selfBalancesRaw);
      const rules = (ensureData?.leavePolicyRules || []).length ? ensureData.leavePolicyRules : fallbackRules;
      const selfBalances = repairLeaveBalanceRows(selfBalancesRaw, rules, assignment || {}, currentEmployeeCode).filter((balance) => belongsToEmployee(balance, currentEmployeeCode));
      const bestEmployeeName = getBestEmployeeDisplayName({ employeeCode: currentEmployeeCode, selfData, assignment, balances: selfBalances, requests: selfRequests }) || currentEmployeeName;

      // 3) Load approval queue only for approver roles, after self load is completed.
      let approvalData = { leaveRequests: [], approvalRequests: [], leaveActivityLog: [], approvalActivityLog: [] };
      if (isApprover) {
        try {
          approvalData = await getLeaveManagementData({
            employeeCode: "",
            role: currentRole,
            viewMode: "approvals",
            legalEntityCode: currentLegalEntityCode,
            departmentCode: currentDepartmentCode,
            workUnitCode: currentWorkUnitCode,
            managerEmployeeCode: currentEmployeeCode,
            includeInactive: false,
          });
        } catch (error) {
          warnings.push(error.message || "Approval queue load failed.");
        }
      }
      if (runId !== loadRunSeqRef.current) return;

      const approvalRowsRaw = approvalData.approvalRequests || approvalData.leaveRequests || [];
      const approvalRows = repairLeaveRequests(approvalRowsRaw).filter((request) => {
        const status = normalizeStatus(request.status);
        return status === "pending" || status === "cancellation requested";
      });
      const approvalLogs = repairActivityLogs(approvalData.approvalActivityLog || approvalData.leaveActivityLog || []);

      // 4) HR Settings is useful for policy name only. It should not block data display.
      let normalizedSettings = {};
      try {
        const settingsData = await getHRSettingsMasterData();
        normalizedSettings = settingsData?.body || settingsData || {};
      } catch (error) {
        // Keep this as non-blocking. Do not show scary message if all leave data loaded.
        normalizedSettings = {};
      }
      if (runId !== loadRunSeqRef.current) return;

      setMasterData(normalizedSettings);
      setCurrentAssignment(assignment);
      setAssignmentLeaveRules(rules);
      setResolvedEmployeeName(bestEmployeeName);
      setMyLeaveRequests(selfRequests);
      setMyLeaveBalances(selfBalances);
      setMyActivityLogs(selfLogs);
      setApprovalRequests(isApprover ? approvalRows : []);
      setApprovalActivityLogs(isApprover ? approvalLogs : []);
      setFormData((previous) => {
        const stillAllowed = rules.some((rule) => normalizeLeaveTypeForCompare(rule.leaveType) === normalizeLeaveTypeForCompare(previous.leaveType));
        return { ...previous, leaveType: stillAllowed ? previous.leaveType : (rules[0]?.leaveType || "") };
      });

      if (!silent) {
        const hasData = selfRequests.length || selfBalances.length || selfLogs.length || approvalRows.length;
        if (warnings.length && !hasData) {
          setMessage(`Leave page loaded with warning: ${warnings.join(" | ")}`);
        } else if (warnings.length && hasData) {
          setMessage("Leave page loaded. Some optional background checks were skipped or delayed.");
        } else {
          setMessage("Leave page loaded successfully.");
        }
      }
    } catch (error) {
      console.error("Leave page load error:", error);
      if (runId === loadRunSeqRef.current) {
        setMessage(error.message || "Failed to load Leave Management data.");
      }
    } finally {
      if (runId === loadRunSeqRef.current) {
        setLoading(false);
      }
    }
  }, [
    currentEmployeeCode,
    currentRole,
    currentLegalEntityCode,
    currentDepartmentCode,
    currentWorkUnitCode,
    isApprover,
  ]);

  useEffect(() => {
    const nextLoadKey = [
      currentEmployeeCode,
      currentRole,
      currentLegalEntityCode,
      currentDepartmentCode,
      currentWorkUnitCode,
    ].join("|");

    if (!currentEmployeeCode || lastAutoLoadKeyRef.current === nextLoadKey) {
      return;
    }

    lastAutoLoadKeyRef.current = nextLoadKey;
    setMyLeaveRequests([]);
    setMyLeaveBalances([]);
    setMyActivityLogs([]);
    setApprovalRequests([]);
    setApprovalActivityLogs([]);
    setCurrentAssignment(null);
    setAssignmentLeaveRules([]);
    setFilterStatus("All");
    setFormData({ employeeName: "", leaveType: "", fromDate: "", toDate: "", reason: "" });
    setResolvedEmployeeName("");
    loadLeavePage();
  }, [currentEmployeeCode, currentRole, currentLegalEntityCode, currentDepartmentCode, currentWorkUnitCode, loadLeavePage]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((previous) => {
      if (name === "fromDate") {
        const normalizedFromDate = getDateInputValue(value);
        const shouldSyncToDate = !previous.toDate || previous.toDate < normalizedFromDate;
        return { ...previous, fromDate: normalizedFromDate, toDate: shouldSyncToDate ? normalizedFromDate : previous.toDate };
      }
      if (name === "toDate") {
        const normalizedToDate = getDateInputValue(value);
        return { ...previous, toDate: previous.fromDate && normalizedToDate < previous.fromDate ? previous.fromDate : normalizedToDate };
      }
      return { ...previous, [name]: value };
    });
  };
  const validateApply = () => {
    if (!currentEmployeeCode.trim()) return "Employee Code is required.";
    if (!formData.leaveType) return "Leave Type is required. Check employee assignment and leave policy rules.";
    if (!formData.fromDate || !formData.toDate) return "From Date and To Date are required.";
    if (leaveDays <= 0) return "To Date should be same as or after From Date.";
    if (!formData.reason.trim()) return "Reason is required.";
    if (!currentAssignment) return "No current assignment found. Please verify EmployeeMaster assignment first.";
    if (!selectedBalance) return "Leave balance is not initialized for this leave type. Refresh and try again.";
    if (overlapWarning) return `Overlapping leave request found from ${formatDate(overlapWarning.fromDate)} to ${formatDate(overlapWarning.toDate)}.`;
    return "";
  };
  const handleApplyLeave = async (event) => {
    event.preventDefault();
    const validation = validateApply();
    if (validation) {
      setMessage(validation);
      return;
    }
    try {
      setSaving(true);
      setMessage("");
      const latestData = await getLeaveManagementData({ employeeCode: currentEmployeeCode, role: "Employee", viewMode: "self", includeInactive: false });
      const latestRequests = repairLeaveRequests(latestData.leaveRequests || []).filter((request) => belongsToEmployee(request, currentEmployeeCode));
      const latestOverlap = findOverlappingLeaveRequest(latestRequests, currentEmployeeCode, formData.fromDate, formData.toDate);
      if (latestOverlap) {
        setMyLeaveRequests(latestRequests);
        setMessage(`Overlapping leave request found from ${formatDate(latestOverlap.fromDate)} to ${formatDate(latestOverlap.toDate)} with status ${latestOverlap.status}.`);
        setSaving(false);
        return;
      }
      const applyResult = await applyLeaveRequest({
        employeeCode: currentEmployeeCode,
        employeeName: formData.employeeName.trim() || resolvedEmployeeName || currentEmployeeName,
        leaveType: formData.leaveType,
        fromDate: formData.fromDate,
        toDate: formData.toDate,
        reason: formData.reason.trim(),
        actionBy: currentEmployeeCode,
        actionByRole: "Employee",
      });
      try {
        await createNotification(buildLeaveApplyNotificationPayload({
          formData: { ...formData, employeeCode: currentEmployeeCode, employeeName: formData.employeeName.trim() || resolvedEmployeeName || currentEmployeeName, reason: formData.reason.trim() },
          applyResult,
          actorRole: "Employee",
        }));
      } catch (notificationError) {
        console.warn("Leave submitted, but notification creation failed:", notificationError);
      }
      setFormData((previous) => ({ ...previous, fromDate: "", toDate: "", reason: "" }));
      await loadLeavePage({ silent: true });
      setMessage("Leave request submitted successfully.");
    } catch (error) {
      console.error("Apply leave error:", error);
      setMessage(error.message || "Failed to submit leave request.");
    } finally {
      setSaving(false);
    }
  };
  const handleStatusAction = async (request, action) => {
    const requestId = getRequestId(request);
    if (!requestId) {
      setMessage("Leave request ID is missing. Refresh and try again.");
      return;
    }
    const label = getActionLabel(action);
    const defaultRemarks = action === "approve" ? "Approved" : action === "reject" ? "Rejected" : action === "cancelPending" ? "Cancelled" : action === "requestCancellation" ? "Cancellation requested" : action === "approveCancellation" ? "Cancellation approved" : action === "rejectCancellation" ? "Cancellation rejected" : "";
    const remarks = window.prompt(`Remarks for ${label}`, defaultRemarks);
    if (remarks === null) return;
    try {
      setActionSavingId(`${requestId}-${action}`);
      setMessage("");
      await updateLeaveRequestStatus({ id: requestId, action, actionBy: getActionBy(currentRole, currentEmployeeCode), actionByRole: currentRole, remarks: remarks || defaultRemarks });
      try {
        await createNotification(buildLeaveStatusNotificationPayload({ request, action, remarks: remarks || defaultRemarks, actorEmployeeCode: getActionBy(currentRole, currentEmployeeCode), actorRole: currentRole }));
      } catch (notificationError) {
        console.warn(`${label} completed, but notification creation failed:`, notificationError);
      }
      await loadLeavePage({ silent: true });
      setMessage(`${label} completed successfully.`);
    } catch (error) {
      console.error("Leave status action error:", error);
      setMessage(error.message || `${label} failed.`);
    } finally {
      setActionSavingId("");
    }
  };
  const renderRequestActions = (request, mode = "self") => {
    const requestId = getRequestId(request);
    const disabled = Boolean(actionSavingId);
    const buttonBase = { ...styles.smallButton };
    if (mode === "approval") {
      if (canApproveOrReject(request, currentRole)) {
        return <div style={styles.actionGroup}><button type="button" style={{ ...buttonBase, ...styles.approveButton }} disabled={disabled} onClick={() => handleStatusAction(request, "approve")}>Approve</button><button type="button" style={{ ...buttonBase, ...styles.rejectButton }} disabled={disabled} onClick={() => handleStatusAction(request, "reject")}>Reject</button>{canCancelPending(request, currentRole, currentEmployeeCode) && <button type="button" style={{ ...buttonBase, ...styles.neutralButton }} disabled={disabled} onClick={() => handleStatusAction(request, "cancelPending")}>Cancel</button>}</div>;
      }
      if (canActOnCancellation(request, currentRole)) {
        return <div style={styles.actionGroup}><button type="button" style={{ ...buttonBase, ...styles.approveButton }} disabled={disabled} onClick={() => handleStatusAction(request, "approveCancellation")}>Approve Cancel</button><button type="button" style={{ ...buttonBase, ...styles.rejectButton }} disabled={disabled} onClick={() => handleStatusAction(request, "rejectCancellation")}>Reject Cancel</button></div>;
      }
      return <span style={styles.noActionText}>No action</span>;
    }
    if (canCancelPending(request, "Employee", currentEmployeeCode)) return <button type="button" style={{ ...buttonBase, ...styles.neutralButton }} disabled={disabled} onClick={() => handleStatusAction(request, "cancelPending")}>Cancel Pending</button>;
    if (canRequestCancellation(request, "Employee", currentEmployeeCode)) return <button type="button" style={{ ...buttonBase, ...styles.neutralButton }} disabled={disabled} onClick={() => handleStatusAction(request, "requestCancellation")}>Request Cancellation</button>;
    if (normalizeStatus(request.status) === "approved" && hasCancellationRequested(request)) return <span style={styles.noActionText}>Cancellation already requested</span>;
    return <span style={styles.noActionText}>No action</span>;
  };

  const displayEmployeeName = resolvedEmployeeName || currentEmployeeName;
  return (
    <div className="fm-hr-page-shell" style={styles.page}>
      <div className="fm-hr-hero" style={styles.header}>
        <div>
          <p style={styles.eyebrow}>HR Module / Leave Management</p>
          <h1 style={styles.title}>Leave Management</h1>
          <p style={styles.subtitle}>Self leave and approval queue are separated. Employee data always uses the logged-in employee; approval queue uses role/scope.</p>
        </div>
        <div style={styles.headerActions}>
          <div style={styles.headerBadge}>🏖️ Leaves & Approvals</div>
          <button type="button" style={styles.secondaryButton} onClick={() => { lastAutoLoadKeyRef.current = ""; loadRunSeqRef.current += 1; loadLeavePage(); }} disabled={loading}>{loading ? "Loading..." : "Refresh"}</button>
        </div>
      </div>
      {message && <div style={message.toLowerCase().includes("success") || message.toLowerCase().includes("loaded") || message.toLowerCase().includes("warning") ? styles.successBox : styles.errorBox}>{message}</div>}
      <section style={styles.panel}>
        <div style={styles.panelHeader}><div><h2 style={styles.panelTitle}>Employee Assignment Resolution</h2><p style={styles.panelSubtitle}>Current logged-in employee self-service context.</p></div></div>
        <div style={styles.assignmentGrid}>
          {isApprover && <Detail label="Current Role" value={currentRole} />}
          <Detail label="Employee Code" value={currentEmployeeCode} />
          {displayEmployeeName && <Detail label="Employee Name" value={displayEmployeeName} />}
          <Detail label="Leave Policy" value={assignmentLeavePolicyId || "Not resolved"} />
          <Detail label="Leave Policy Name" value={assignedLeavePolicyName || "-"} />
          {assignmentAttendancePolicyId && <Detail label="Attendance Policy" value={assignmentAttendancePolicyId} />}
          {currentAssignment?.holidayCalendarId && <Detail label="Holiday Calendar" value={currentAssignment.holidayCalendarId} />}
          {currentAssignment?.weeklyOffPatternId && <Detail label="Weekly Off Pattern" value={currentAssignment.weeklyOffPatternId} />}
          <Detail label="Leave Rules Loaded" value={assignmentLeaveRules.length} />
          <Detail label="Leave Policies Loaded" value={loadedLeavePolicyCount} />
        </div>
      </section>
      <div style={styles.summaryGrid}>
        <SummaryCard icon="🧮" label="My Available Balance" value={summary.myAvailable} />
        <SummaryCard icon="⏳" label="My Pending Requests" value={summary.myPending} />
        {isApprover && <SummaryCard icon="📥" label="Approval Pending" value={summary.approvalPending} />}
        {isApprover && <SummaryCard icon="🔁" label="Cancel Approval" value={summary.approvalCancellation} />}
        {isApprover && <SummaryCard icon="✅" label="Approval Queue Total" value={summary.approvalsTotal} />}
      </div>
      <div style={styles.mainGrid}>
        <section style={styles.panel}>
          <div style={styles.panelHeader}><div><h2 style={styles.panelTitle}>Apply Leave</h2><p style={styles.panelSubtitle}>Applies only for current logged-in employee: {currentEmployeeCode}.</p></div></div>
          <form onSubmit={handleApplyLeave} style={styles.form}>
            <div style={styles.fieldGrid}>
              <label style={styles.field}><span style={styles.label}>Employee Code</span><input style={styles.input} value={currentEmployeeCode} readOnly /></label>
              <label style={styles.field}><span style={styles.label}>Employee Name</span><input style={styles.input} name="employeeName" value={formData.employeeName || displayEmployeeName} onChange={handleInputChange} placeholder="Auto from employee profile" readOnly={Boolean(displayEmployeeName)} /></label>
              <label style={styles.field}><span style={styles.label}>Leave Type</span><select style={styles.input} name="leaveType" value={formData.leaveType} onChange={handleInputChange} disabled={assignmentLeaveRules.length === 0}>{assignmentLeaveRules.length === 0 && <option value="">No leave rules found</option>}{assignmentLeaveRules.map((rule) => <option key={`${rule.leavePolicyId}-${rule.leaveType}`} value={rule.leaveType}>{rule.leaveType}</option>)}</select></label>
              <label style={styles.field}><span style={styles.label}>Calendar Days</span><input style={styles.input} value={leaveDays} readOnly /></label>
              <label style={styles.field}><span style={styles.label}>From Date</span><input style={styles.input} type="date" name="fromDate" value={formData.fromDate} onChange={handleInputChange} /></label>
              <label style={styles.field}><span style={styles.label}>To Date</span><input style={styles.input} type="date" name="toDate" value={formData.toDate} min={formData.fromDate || undefined} onChange={handleInputChange} /></label>
            </div>
            <label style={styles.field}><span style={styles.label}>Reason</span><textarea style={styles.textarea} name="reason" value={formData.reason} onChange={handleInputChange} rows="4" placeholder="Enter reason for leave" /></label>
            <div style={styles.infoBox}><strong>Available preview:</strong> {selectedBalance ? `${getBalanceAvailable(selectedBalance)} day(s) for ${formData.leaveType}` : "Balance row not loaded. Refresh to run EnsureLeaveBalances."}{overlapWarning && <div style={styles.warningText}>Overlap warning: existing {overlapWarning.status} leave from {formatDate(overlapWarning.fromDate)} to {formatDate(overlapWarning.toDate)}.</div>}{insufficientBalanceWarning && <div style={styles.warningText}>Balance warning: requested {leaveDays}, available {getBalanceAvailable(selectedBalance)}.</div>}<div style={styles.mutedText}>Backend calculates final chargeable days after holiday and weekly-off exclusion.</div></div>
            <button type="submit" style={styles.primaryButton} disabled={saving || Boolean(overlapWarning)}>{saving ? "Submitting..." : overlapWarning ? "Resolve Overlap Before Submit" : "Submit Leave Request"}</button>
          </form>
        </section>
        <section style={styles.panel}>
          <div style={styles.panelHeader}><div><h2 style={styles.panelTitle}>My Leave Balances</h2><p style={styles.panelSubtitle}>Only current employee balances.</p></div></div>
          <div style={styles.balanceList}>{myLeaveBalances.length === 0 && <div style={styles.emptyBox}>No balance rows loaded yet.</div>}{myLeaveBalances.map((item) => <BalanceCard key={`${item.employeeCode}-${item.leavePolicyId}-${item.leaveType}-${item.leaveYear}`} item={item} />)}</div>
        </section>
      </div>
      <RequestTable title="My Leave Requests" subtitle="Only current employee leave records." rows={myLeaveRequests} renderActions={(request) => renderRequestActions(request, "self")} filterStatus="All" setFilterStatus={null} />
      {isApprover && <RequestTable title="Approval Queue" subtitle="Pending and cancellation requests under your approval scope." rows={approvalFilteredRequests} renderActions={(request) => renderRequestActions(request, "approval")} filterStatus={filterStatus} setFilterStatus={setFilterStatus} />}
      <ActivityTable title="My Leave Activity Log" rows={myActivityLogs} />
      {isApprover && <ActivityTable title="Approval Activity Log" rows={approvalActivityLogs} />}
    </div>
  );
}
function BalanceCard({ item }) {
  const available = getBalanceAvailable(item);
  const entitled = toNumber(item.entitledDays ?? item.entitled, 0);
  const used = toNumber(item.usedDays ?? item.used, 0);
  const pending = toNumber(item.pendingDays ?? item.pending, 0);
  const percent = entitled > 0 ? Math.min(100, Math.round(((used + pending) / entitled) * 100)) : 0;
  return <div style={styles.balanceCard}><div style={styles.balanceTop}><strong>{item.leaveType}</strong><span style={styles.balanceAvailable}>{available} left</span></div><div style={styles.mutedText}>{item.employeeCode} · {item.leavePolicyId} · {item.leaveYear}{item.isPreview ? " · preview" : ""}</div><div style={styles.mutedText}>Category: {normalizeTextField(item.leaveCategory) || "-"}</div><div style={styles.progressTrack}><div style={{ ...styles.progressFill, width: `${percent}%` }} /></div><div style={styles.balanceMeta}><span>Entitled: {entitled}</span><span>Used: {used}</span><span>Pending: {pending}</span></div></div>;
}
function RequestTable({ title, subtitle, rows, renderActions, filterStatus, setFilterStatus }) {
  return <section style={styles.panel}><div style={styles.tableHeader}><div><h2 style={styles.panelTitle}>{title}</h2><p style={styles.panelSubtitle}>{subtitle}</p></div>{setFilterStatus && <select style={styles.filterSelect} value={filterStatus} onChange={(event) => setFilterStatus(event.target.value)}>{STATUS_OPTIONS.map((status) => <option key={status}>{status}</option>)}</select>}</div><div style={styles.tableWrapper}><table style={styles.table}><thead><tr><th style={styles.th}>Employee</th><th style={styles.th}>Leave</th><th style={styles.th}>Date Range</th><th style={styles.th}>Days</th><th style={styles.th}>Reason</th><th style={styles.th}>Status</th><th style={styles.th}>Actions</th></tr></thead><tbody>{rows.length === 0 && <tr><td style={styles.emptyCell} colSpan="7">No leave requests found.</td></tr>}{rows.map((request) => <tr key={getRequestId(request) || `${request.employeeCode}-${request.fromDate}-${request.toDate}`}><td style={styles.td}><strong>{request.employeeName || "-"}</strong><div style={styles.mutedText}>{request.employeeCode}</div></td><td style={styles.td}>{request.leaveType}<div style={styles.mutedText}>{request.leavePolicyId}</div><div style={styles.mutedText}>{normalizeTextField(request.leaveCategory) || "-"}</div></td><td style={styles.td}>{formatDate(request.fromDate)} to {formatDate(request.toDate)}<div style={styles.mutedText}>Applied: {formatDate(request.appliedOn)}</div></td><td style={styles.td}><div><strong>Chargeable:</strong> {request.chargeableDays ?? request.days ?? request.requestedDays}</div><div style={styles.mutedText}><strong>Calendar:</strong> {request.requestedDays}</div></td><td style={styles.td}>{request.reason}</td><td style={styles.td}><span style={{ ...styles.statusBadge, ...getStatusStyle(request.status) }}>{request.status}</span></td><td style={styles.td}>{renderActions(request)}</td></tr>)}</tbody></table></div></section>;
}
function ActivityTable({ title, rows }) {
  return <section style={styles.panel}><div style={styles.tableHeader}><div><h2 style={styles.panelTitle}>{title}</h2><p style={styles.panelSubtitle}>Loaded from HR_LeaveActivityLog.</p></div></div><div style={styles.tableWrapper}><table style={styles.activityTable}><thead><tr><th style={styles.th}>Action On</th><th style={styles.th}>Action</th><th style={styles.th}>Employee</th><th style={styles.th}>Leave</th><th style={styles.th}>Status Change</th><th style={styles.th}>Action By</th><th style={styles.th}>Remarks</th></tr></thead><tbody>{rows.length === 0 && <tr><td style={styles.emptyCell} colSpan="7">No leave activity loaded yet.</td></tr>}{rows.map((log) => <tr key={log.id || `${log.employeeCode}-${log.actionOn}-${log.actionType}`}><td style={styles.td}>{formatDate(log.actionOn)}</td><td style={styles.td}>{log.actionType}</td><td style={styles.td}>{log.employeeCode}</td><td style={styles.td}>{log.leaveType}<div style={styles.mutedText}>{log.days} day(s)</div></td><td style={styles.td}>{log.previousStatus} → {log.newStatus}</td><td style={styles.td}>{log.actionBy}<div style={styles.mutedText}>{log.actionByRole}</div></td><td style={styles.td}>{log.remarks}</td></tr>)}</tbody></table></div></section>;
}
function SummaryCard({ icon, label, value }) {
  return <div style={styles.summaryCard}><div style={styles.summaryIcon}>{icon}</div><div><p style={styles.summaryLabel}>{label}</p><h2 style={styles.summaryValue}>{value}</h2></div></div>;
}
function Detail({ label, value }) {
  return <div style={styles.detailItem}><div style={styles.detailKey}>{label}</div><div style={styles.detailValue}>{value || "-"}</div></div>;
}
const styles = {
  page: { fontFamily: "Segoe UI, Calibri, Arial, sans-serif", padding: "18px", background: "var(--fm-content)", minHeight: "100vh", color: "var(--fm-text)", boxSizing: "border-box", overflowX: "hidden" },
  header: { display: "flex", justifyContent: "space-between", gap: "18px", alignItems: "flex-start", flexWrap: "wrap", marginBottom: "16px", background: "var(--fm-hero-bg)", color: "#ffffff", borderRadius: "20px", padding: "22px 24px", boxShadow: "0 10px 28px rgba(0,80,150,0.16)" },
  headerActions: { display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" },
  eyebrow: { margin: 0, color: "rgba(255,255,255,0.85)", fontWeight: 800, fontSize: "11px", letterSpacing: "0.09em", textTransform: "uppercase" },
  title: { margin: "5px 0", fontSize: "29px", lineHeight: 1.2, color: "#ffffff" },
  subtitle: { margin: 0, color: "rgba(255,255,255,0.92)", maxWidth: "820px", lineHeight: 1.5 },
  headerBadge: { background: "rgba(255,255,255,0.18)", border: "1px solid rgba(255,255,255,0.42)", borderRadius: "999px", padding: "9px 13px", fontWeight: 800, color: "#ffffff" },
  primaryButton: { border: "1px solid #0078d4", background: "var(--fm-page-accent)", color: "#ffffff", borderRadius: "10px", padding: "9px 13px", fontWeight: 750, cursor: "pointer", fontSize: "12.5px" },
  secondaryButton: { background: "#eef6ff", color: "var(--fm-page-accent)", border: "1px solid #bfdbfe", borderRadius: "10px", padding: "9px 13px", fontWeight: 750, cursor: "pointer", fontSize: "12.5px" },
  successBox: { border: "1px solid #bbf7d0", background: "#ecfdf5", color: "#047857", borderRadius: "13px", padding: "11px 13px", marginBottom: "14px", fontWeight: 700 },
  errorBox: { border: "1px solid #fecaca", background: "#fef2f2", color: "#b91c1c", borderRadius: "13px", padding: "11px 13px", marginBottom: "14px", fontWeight: 700 },
  warningText: { marginTop: "8px", color: "#b45309", fontWeight: 800 },
  summaryGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(165px, 1fr))", gap: "11px", marginBottom: "14px", minWidth: 0 },
  summaryCard: { display: "flex", alignItems: "center", gap: "12px", background: "var(--fm-card)", border: "1px solid var(--fm-border)", borderRadius: "17px", padding: "13px 14px", boxShadow: "0 3px 13px rgba(15,23,42,0.052)", minWidth: 0 },
  summaryIcon: { width: "42px", height: "42px", borderRadius: "14px", background: "#eef6ff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "21px", flex: "0 0 auto" },
  summaryLabel: { margin: 0, color: "var(--fm-muted)", fontSize: "11.5px", fontWeight: 700 },
  summaryValue: { margin: "4px 0 0", fontSize: "25px", color: "var(--fm-page-accent)" },
  panel: { background: "var(--fm-card)", border: "1px solid var(--fm-border)", borderRadius: "18px", padding: "16px", boxShadow: "0 3px 13px rgba(15,23,42,0.052)", overflow: "hidden", marginBottom: "14px", minWidth: 0 },
  panelHeader: { display: "flex", justifyContent: "space-between", gap: "12px", alignItems: "flex-start", marginBottom: "12px", flexWrap: "wrap" },
  panelTitle: { margin: 0, fontSize: "18px", color: "var(--fm-text)" },
  panelSubtitle: { margin: "4px 0 0", color: "var(--fm-muted)", fontSize: "13px" },
  mainGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: "14px", marginBottom: "14px", minWidth: 0 },
  form: { display: "grid", gap: "14px" },
  fieldGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: "12px", minWidth: 0 },
  field: { display: "grid", gap: "6px", minWidth: 0 },
  label: { fontWeight: 800, fontSize: "12.5px", color: "var(--fm-text)" },
  input: { width: "100%", border: "1px solid var(--fm-border)", borderRadius: "12px", padding: "10px 11px", fontSize: "13px", outline: "none", boxSizing: "border-box", background: "var(--fm-card)", minWidth: 0 },
  textarea: { width: "100%", border: "1px solid var(--fm-border)", borderRadius: "12px", padding: "10px 11px", fontSize: "13px", outline: "none", resize: "vertical", boxSizing: "border-box", fontFamily: "inherit" },
  infoBox: { background: "#eef6ff", border: "1px solid #bfdbfe", color: "var(--fm-page-accent)", borderRadius: "14px", padding: "12px", fontSize: "12.5px", fontWeight: 700 },
  assignmentGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "10px" },
  detailItem: { border: "1px solid var(--fm-border)", borderRadius: "12px", background: "#f8fafc", padding: "10px", minWidth: 0 },
  detailKey: { color: "var(--fm-muted)", fontSize: "11.5px", fontWeight: 800, marginBottom: "6px" },
  detailValue: { color: "var(--fm-text)", fontSize: "12.5px", fontWeight: 800, wordBreak: "break-word" },
  balanceList: { display: "grid", gap: "12px" },
  balanceCard: { border: "1px solid var(--fm-border)", borderRadius: "14px", padding: "14px", background: "#f8fafc" },
  balanceTop: { display: "flex", justifyContent: "space-between", gap: "10px", alignItems: "center", marginBottom: "8px", flexWrap: "wrap" },
  balanceAvailable: { fontWeight: 800, color: "var(--fm-page-accent)" },
  progressTrack: { height: "8px", background: "#e2e8f0", borderRadius: "999px", overflow: "hidden", marginTop: "10px" },
  progressFill: { height: "100%", background: "var(--fm-page-accent)", borderRadius: "999px" },
  balanceMeta: { display: "flex", justifyContent: "space-between", gap: "8px", flexWrap: "wrap", marginTop: "10px", color: "var(--fm-muted)", fontSize: "12px" },
  tableHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", flexWrap: "wrap", marginBottom: "14px" },
  filterSelect: { border: "1px solid var(--fm-border)", borderRadius: "12px", padding: "10px 12px", background: "var(--fm-card)", fontWeight: 700 },
  tableWrapper: { width: "100%", overflowX: "auto", border: "1px solid var(--fm-border)", borderRadius: "14px" },
  table: { width: "100%", borderCollapse: "collapse", minWidth: "1100px" },
  activityTable: { width: "100%", borderCollapse: "collapse", minWidth: "1000px" },
  th: { textAlign: "left", padding: "10px", background: "#f8fafc", color: "var(--fm-text)", fontSize: "12.5px", borderBottom: "1px solid var(--fm-border)", whiteSpace: "nowrap", fontWeight: 800 },
  td: { padding: "10px", borderBottom: "1px solid var(--fm-border)", verticalAlign: "top", fontSize: "12.5px", whiteSpace: "nowrap", color: "var(--fm-text)" },
  mutedText: { color: "var(--fm-muted)", fontSize: "12px", marginTop: "4px" },
  statusBadge: { display: "inline-flex", alignItems: "center", borderRadius: "999px", padding: "5px 9px", fontSize: "11.5px", fontWeight: 800 },
  noActionText: { color: "var(--fm-muted)", fontSize: "12.5px", fontWeight: 800 },
  emptyCell: { padding: "22px", textAlign: "center", color: "var(--fm-muted)" },
  emptyBox: { padding: "14px", border: "1px dashed #cbd5e1", borderRadius: "12px", color: "var(--fm-muted)", textAlign: "center" },
  actionGroup: { display: "flex", gap: "6px", flexWrap: "wrap" },
  smallButton: { border: "1px solid var(--fm-border)", borderRadius: "999px", padding: "7px 10px", fontWeight: 800, fontSize: "11.5px", cursor: "pointer", background: "var(--fm-card)" },
  approveButton: { background: "#ecfdf5", color: "#047857", borderColor: "#bbf7d0" },
  rejectButton: { background: "#fef2f2", color: "#b91c1c", borderColor: "#fecaca" },
  neutralButton: { background: "#f8fafc", color: "var(--fm-text)", borderColor: "#cbd5e1" },
};export default LeaveManagement;
